import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";
import { ORDER_STATE_N2 } from "../../../config/constants";

/**
 * Relanza un error de Axios como Error con el mensaje legible para el operario
 * @param {Object} error - Error capturado de Axios
 * @param {string} fallback - Mensaje genérico propio del método
 * @throws {Error} Error con .message y .status (undefined si no hubo respuesta)
 */
const relanzar = (error, fallback) => {
    const wrapped = new Error(error.response?.data?.message || fallback);
    wrapped.status = error.response?.status;
    throw wrapped;
};

/**
 * Servicio para operaciones de ventas del feature "ordenes"
 */
class VentasService {
    /**
     * Avisar al personal de almacén de que hay pedidos pendientes de preparar.
     * Envía una notificación push a todos los usuarios con rol almacen.
     * No comprueba si existen pedidos pendientes ni limita la frecuencia: cada
     * llamada reparte un aviso.
     * @returns {Promise<{avisados: number}>} Usuarios de almacén avisados
     */
    static async avisarPreparacion() {
        const response = await axios.post(
            HostService.getHost() + 'ventas/avisar-preparacion',
            {},
            AuthService.setToken()
        );
        return response.data.data;
    }

    /**
     * Forzar como preparado un pedido colgado de Nave 2, para sacarlo del listado.
     * Con `forzado` el backend no avisa al preparador de Nave 1: nadie lo ha punteado
     * en Nave 2 y el aviso "ya está preparado" sería falso.
     * @param {string} numero - Documento en formato "SERIE-NUMERO"
     * @param {number} ejercicio - Año del ejercicio
     * @param {string} tipo - Tipo de documento (albaran|pedido, el backend lo normaliza)
     * @returns {Promise<Object>} Registro de Nave 2 resultante
     */
    static async forzarPreparadoN2(numero, ejercicio, tipo) {
        const response = await axios.post(
            HostService.getHost() + 'ventas/n2/asignar',
            { estado: ORDER_STATE_N2.PREPARADO, forzado: true },
            { ...AuthService.setToken(), params: { pedido: numero, ejercicio, tipo } }
        );
        return response.data.data;
    }

    /**
     * Últimos pedidos/albaranes marcados como preparados en Nave 2, del más reciente
     * al más antiguo.
     * @param {number} ejercicio - Año del ejercicio
     * @param {number} [limite=50] - Número máximo de pedidos a devolver (1-200)
     * @returns {Promise<Array<{numero: string, tipo: string, preparado: (string|null),
     *   registrado: string, cliente: (string|null), ruta: (string|null)}>>} Pedidos preparados
     */
    static async getPreparadosN2(ejercicio, limite = 50) {
        const response = await axios.get(
            HostService.getHost() + 'ventas/n2/preparados',
            { ...AuthService.setToken(), params: { ejercicio, limite } }
        );
        return response.data.data;
    }

    /**
     * Estado de los pedidos y albaranes de venta de Nave 1 pendientes de preparar.
     * Un registro por documento (tipo + serie + pedido); `almacen` es un atributo del
     * documento, no multiplica las filas.
     * DECISION: endpoint legacy sin documentar en api/docs/api/ventas.md (GET ventas/:ano?/estados,
     * ../api/src/routes/ventas.routes.js). Devuelve el array directo, sin envoltura { data }.
     * Si se migra al módulo ventas cambiará la envoltura. El backend ignora el año de la URL
     * (el parámetro de ruta es `ano` y el controlador lee `ejercicio`): siempre usa el año actual.
     * Acceso: solo token (sin roles en backend); la restricción a ADMIN vive en routes.js.
     * @param {number} ejercicio - Año del ejercicio
     * @returns {Promise<Array<{serie: string, pedido: string, tipo: string, estado: string,
     *   ejercicio: number, preparado: (string|null), revisado: (string|null), camara: (string|null),
     *   fecha: string, almacen: string, transporte: (string|null),
     *   EtiquetasModels: Array<{ruta: (string|null), cliente: (string|null)}>}>>}
     *   Ventas tal cual las entrega el backend ([] si la respuesta no es un array)
     * @throws {Error} Con .message legible y .status HTTP (undefined sin respuesta)
     */
    static async getEstados(ejercicio) {
        try {
            const response = await axios.get(
                HostService.getHost() + `ventas/${ejercicio}/estados`,
                AuthService.setToken()
            );
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            relanzar(error, 'Error al obtener los estados de ventas');
        }
    }

    /**
     * Cambia el estado de preparación de una venta de Nave 1.
     * DECISION: endpoint legacy sin documentar (POST ventas/asignar, multipart con multer).
     * Envía lo mismo que OrderService.assignToOrder para un cambio de estado: query
     * pedido/ejercicio/tipo y solo `estado` en FormData (sin Content-Type manual: Axios pone
     * el boundary). Responde 201 con el registro FinalVentas, sin envoltura { data }; 400
     * (formato de pedido), 404 (no existe o ya traspasado), 409 y 500 llevan { message }.
     * Si se migra al módulo ventas cambiará la envoltura.
     * @param {string} numero - Documento en formato "SERIE-NUMERO"
     * @param {number} ejercicio - Año del ejercicio
     * @param {string} tipo - Tipo de documento (ALBARAN|PEDIDO, el backend lo pasa a mayúsculas)
     * @param {string} estado - Valor de ORDER_STATE (Nave 1)
     * @returns {Promise<Object>} Registro de ventas resultante, tal cual lo devuelve el backend
     * @throws {Error} Con .message legible y .status HTTP (undefined sin respuesta)
     */
    static async asignarEstado(numero, ejercicio, tipo, estado) {
        const formData = new FormData();
        formData.append('estado', estado);
        try {
            const response = await axios.post(
                HostService.getHost() + 'ventas/asignar',
                formData,
                { ...AuthService.setToken(), params: { pedido: numero, ejercicio, tipo } }
            );
            return response.data;
        } catch (error) {
            relanzar(error, 'Error al cambiar el estado del pedido');
        }
    }
}

export default VentasService;
